/**
 * Feb 2026 · Supplier Master — bespoke dialog with multi-address editor.
 *
 * Mirrors CustomerFormDialog but with supplier-flavour labels
 * (Bill From / Ship From / Warehouse / Factory / Registered Office /
 * Other) and supplier-flavour top fields (short_code / payment_terms
 * / lead_time_days). Posts directly to /suppliers so the generic
 * schema-driven form doesn't have to model repeatable addresses.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { PlusIcon, TrashIcon, StarIcon, TruckIcon, WarningIcon, CheckCircleIcon } from "@phosphor-icons/react";
// Feb 2026 · Shared GST helpers · SupplierFormDialog now validates GSTIN
// with the EXACT same 15-char regex, state-code map and PAN auto-fill
// that CustomerFormDialog uses, so procurement + tax teams see one rule.
import { GST_STATE_CODES, isValidGstin, deriveGstFields } from "@/lib/gst";

const inp = "w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
const area = "w-full min-h-[56px] px-2.5 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] resize-y";
const uid = () => (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));

const LABELS = ["Bill From", "Ship From", "Warehouse", "Factory", "Registered Office", "Other"];

const emptyAddress = (label = "Bill From") => ({
  id: uid(), label, custom_label: "", address: "", city: "", state: "",
  state_code: "", pincode: "", country: "India", gstin: "", pan: "",
  contact_person: "", phone: "", email: "",
  is_primary_bill: false, is_primary_ship: false,
});

const emptySupplier = () => ({
  code: "", name: "", short_code: "", contact_person: "", email: "", phone: "",
  // Feb 2026 · GST fields mirror the Customer Master so downstream POs +
  // reports can rely on identical shape.
  gstin: "", state_code: "", pan: "",
  city: "", state: "", country: "India", address: "",
  payment_terms: "", lead_time_days: 0, is_active: true, description: "",
  addresses: [{ ...emptyAddress("Bill From"), is_primary_bill: true, is_primary_ship: true }],
});

export default function SupplierFormDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(emptySupplier());
  const [saving, setSaving] = useState(false);
  // Feb 2026 · Per-address duplicate-GSTIN warnings, keyed by index.
  // Populated live as the operator types; save is not hard-blocked so
  // legit multi-branch GSTIN reuses still go through, but the warning
  // makes the collision undeniable.
  const [dupWarn, setDupWarn] = useState({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      if (editing) {
        const clone = JSON.parse(JSON.stringify(editing));
        if (!Array.isArray(clone.addresses) || clone.addresses.length === 0) {
          clone.addresses = [{ ...emptyAddress("Bill From"), is_primary_bill: true, is_primary_ship: true }];
        } else {
          clone.addresses = clone.addresses.map(a => ({ ...emptyAddress(), ...a, id: a.id || uid() }));
        }
        setForm({ ...emptySupplier(), ...clone });
      } else {
        try {
          const { data } = await api.get("/next/supplier-code");
          setForm({ ...emptySupplier(), code: data?.code || "" });
        } catch { setForm(emptySupplier()); }
      }
    })();
  }, [open, editing]);

  const updateAddr = (idx, patch) => setForm(f => ({
    ...f, addresses: f.addresses.map((a, i) => i === idx ? { ...a, ...patch } : a),
  }));
  const addAddress = () => setForm(f => ({
    ...f, addresses: [...f.addresses, emptyAddress(f.addresses.length ? "Ship From" : "Bill From")],
  }));
  const deleteAddress = (idx) => setForm(f => {
    if (f.addresses.length <= 1) { toast.error("At least one address required"); return f; }
    return { ...f, addresses: f.addresses.filter((_, i) => i !== idx) };
  });
  const setPrimaryBill = (idx) => setForm(f => ({
    ...f, addresses: f.addresses.map((a, i) => ({ ...a, is_primary_bill: i === idx })),
  }));
  const setPrimaryShip = (idx) => setForm(f => ({
    ...f, addresses: f.addresses.map((a, i) => ({ ...a, is_primary_ship: i === idx })),
  }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Supplier name is required");
    if (!form.addresses.some(a => a.is_primary_bill)) return toast.error("Mark one address as primary Bill From");
    if (!form.addresses.some(a => a.is_primary_ship)) return toast.error("Mark one address as primary Ship From");
    // Feb 2026 · Mandatory GSTIN + State Code on the primary Bill From
    // address, parity with Customer Master rules so no PO can ever be
    // raised against a tax-blank supplier.
    const pb = form.addresses.find(a => a.is_primary_bill) || form.addresses[0];
    if (!(pb.gstin || "").trim()) return toast.error("GSTIN is required on the primary Bill From address");
    if (!isValidGstin(pb.gstin)) return toast.error("GSTIN on the primary Bill From address is not a valid 15-character code");
    if (!(pb.state_code || "").trim() || pb.state_code.length !== 2) return toast.error("2-digit State Code is required on the primary Bill From address");
    // Structural check on every OTHER address too — empty stays fine
    // (only the primary is mandatory) but a typed value must be valid.
    for (let i = 0; i < form.addresses.length; i++) {
      const a = form.addresses[i];
      if ((a.gstin || "").trim() && !isValidGstin(a.gstin)) {
        return toast.error(`Address #${i + 1} has an invalid GSTIN — fix or clear it before saving`);
      }
    }
    setSaving(true);
    try {
      // Denorm the primary Bill From back onto the flat fields so downstream
      // legacy reports (RM Master price list, PO scaffolding) that still read
      // supplier.gstin / .address keep working. GST-specific fields
      // (state_code / PAN) are propagated as well.
      const payload = {
        ...form,
        gstin: pb.gstin || form.gstin,
        state_code: pb.state_code || form.state_code,
        pan: pb.pan || form.pan,
        address: pb.address || form.address,
        city: pb.city || form.city, state: pb.state || form.state,
      };
      if (editing) await api.put(`/suppliers/${editing.id}`, payload);
      else         await api.post("/suppliers/", payload);
      toast.success(`Supplier ${editing ? "updated" : "created"}`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-5xl max-h-[88vh] overflow-y-auto"
        description="Add or edit a supplier with multiple Bill From / Ship From addresses.">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} Supplier</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><label className="text-[10px] uppercase text-zinc-500">Code</label>
            <input data-testid="sup-code" value={form.code} readOnly className={inp + " opacity-60"} /></div>
          <div className="md:col-span-2"><label className="text-[10px] uppercase text-zinc-500">Supplier Name *</label>
            <input data-testid="sup-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inp} /></div>
          <div><label className="text-[10px] uppercase text-zinc-500">Short Code</label>
            <input data-testid="sup-short" value={form.short_code} onChange={e => setForm(f => ({ ...f, short_code: e.target.value.toUpperCase() }))} placeholder="e.g. WAMPUM" className={inp} /></div>
          <div><label className="text-[10px] uppercase text-zinc-500">Contact Person</label>
            <input value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} className={inp} /></div>
          <div><label className="text-[10px] uppercase text-zinc-500">Phone</label>
            <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={inp} /></div>
          <div><label className="text-[10px] uppercase text-zinc-500">Email</label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inp} /></div>
          <div><label className="text-[10px] uppercase text-zinc-500">Payment Terms</label>
            <input value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} placeholder="e.g. Net 30" className={inp} /></div>
          <div><label className="text-[10px] uppercase text-zinc-500">Lead Time (days)</label>
            <input type="number" value={form.lead_time_days} onChange={e => setForm(f => ({ ...f, lead_time_days: Number(e.target.value) || 0 }))} className={inp} /></div>
          <div className="flex items-end gap-2 pb-1">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              Active
            </label>
          </div>
          <div className="md:col-span-3"><label className="text-[10px] uppercase text-zinc-500">Notes</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={area} /></div>
        </div>

        {/* Address Editor */}
        <div className="mt-4 border-t border-[var(--border)] pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display text-sm font-bold">Addresses <span className="text-[10px] text-zinc-500 font-normal">· one Bill From + one Ship From must be marked primary</span></div>
            <button data-testid="sup-add-addr" onClick={addAddress} className="h-8 px-3 border border-[var(--accent)]/60 text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs rounded-sm flex items-center gap-1">
              <PlusIcon size={12} weight="bold" />Add Address
            </button>
          </div>
          <div className="space-y-3">
            {form.addresses.map((a, i) => (
              <div key={a.id} data-testid={`sup-addr-${i}`} className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                  <div><label className="text-[9px] uppercase text-zinc-500">Label</label>
                    <select value={a.label} onChange={e => updateAddr(i, { label: e.target.value })} className={inp}>
                      {LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  {a.label === "Other" && (
                    <div><label className="text-[9px] uppercase text-zinc-500">Custom Label</label>
                      <input value={a.custom_label} onChange={e => updateAddr(i, { custom_label: e.target.value })} className={inp} /></div>
                  )}
                  <div>
                    <label className="text-[9px] uppercase text-zinc-500 flex items-center gap-1">
                      GSTIN {a.is_primary_bill && <span className="text-red-400">*</span>}
                      {a.gstin && (isValidGstin(a.gstin)
                        ? <CheckCircleIcon size={11} weight="fill" className="text-emerald-400" title="Valid 15-char GSTIN" />
                        : <WarningIcon size={11} weight="fill" className="text-red-400" title="Invalid GSTIN structure" />)}
                    </label>
                    <input
                      value={a.gstin}
                      onChange={e => {
                        const raw = (e.target.value || "").toUpperCase().replace(/\s+/g, "");
                        updateAddr(i, deriveGstFields(raw, a));
                        // Kick off the duplicate probe only for structurally
                        // valid GSTINs — no point hammering the endpoint
                        // for half-typed strings. Errors are swallowed so
                        // a flaky endpoint never blocks the operator.
                        if (isValidGstin(raw)) {
                          (async () => {
                            try {
                              const { data } = await api.get("/suppliers/check-gstin", {
                                params: { gstin: raw, exclude_id: editing?.id || "" },
                              });
                              setDupWarn(prev => {
                                const next = { ...prev };
                                if (data?.exists) next[i] = data.supplier;
                                else delete next[i];
                                return next;
                              });
                            } catch { /* ignore */ }
                          })();
                        } else {
                          setDupWarn(prev => { const n = { ...prev }; delete n[i]; return n; });
                        }
                      }}
                      maxLength={15}
                      placeholder="27AAAAA0000A1Z5"
                      className={`${inp} font-mono ${a.gstin && !isValidGstin(a.gstin) ? "border-red-500/60 focus:border-red-500" : ""}`}
                      data-testid={`sup-addr-gstin-${i}`}
                    />
                    {a.gstin && !isValidGstin(a.gstin) && (
                      <div className="mt-1 text-[10px] text-red-400" data-testid={`sup-addr-gstin-error-${i}`}>
                        Invalid GSTIN — must be 15 chars: 2-digit state, 5-letter PAN prefix, 4-digit seq, PAN check letter, entity, "Z", check digit.
                      </div>
                    )}
                    {a.gstin && isValidGstin(a.gstin) && GST_STATE_CODES[a.gstin.slice(0, 2)] && (
                      <div className="mt-1 text-[10px] text-emerald-400" data-testid={`sup-addr-gstin-state-hint-${i}`}>
                        State Code {a.gstin.slice(0, 2)} · {GST_STATE_CODES[a.gstin.slice(0, 2)]}
                      </div>
                    )}
                    {dupWarn[i] && (
                      <div
                        className="mt-1 flex items-start gap-1 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-sm px-2 py-1"
                        data-testid={`sup-addr-gstin-dup-${i}`}
                      >
                        <WarningIcon size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
                        <span>Already registered to <b>{dupWarn[i].code} · {dupWarn[i].name}</b> ({dupWarn[i].address_label}). Duplicate supplier GSTINs can cross-book payables — verify before saving.</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-[9px] uppercase text-zinc-500">State Code {a.is_primary_bill && <span className="text-red-400">*</span>}</label>
                    <input
                      value={a.state_code}
                      onChange={e => updateAddr(i, { state_code: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) })}
                      maxLength={2}
                      inputMode="numeric"
                      placeholder="27"
                      className={inp + " font-mono"}
                      data-testid={`sup-addr-state-code-${i}`}
                    />
                  </div>
                  <div>
                    <label className="text-[9px] uppercase text-zinc-500">PAN <span className="text-zinc-500 text-[9px] normal-case">(auto)</span></label>
                    <input
                      value={a.pan}
                      onChange={e => updateAddr(i, { pan: e.target.value.toUpperCase().slice(0, 10) })}
                      maxLength={10}
                      placeholder="AAAAA0000A"
                      className={inp + " font-mono"}
                      data-testid={`sup-addr-pan-${i}`}
                    />
                  </div>
                </div>
                <div><label className="text-[9px] uppercase text-zinc-500">Address</label>
                  <textarea value={a.address} onChange={e => updateAddr(i, { address: e.target.value })} className={area} /></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  <input placeholder="City" value={a.city} onChange={e => updateAddr(i, { city: e.target.value })} className={inp} />
                  <input placeholder="State" value={a.state} onChange={e => updateAddr(i, { state: e.target.value })} className={inp} />
                  <input placeholder="Pincode" value={a.pincode} onChange={e => updateAddr(i, { pincode: e.target.value })} className={inp} />
                  <input placeholder="Country" value={a.country} onChange={e => updateAddr(i, { country: e.target.value })} className={inp} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                  <input placeholder="Contact Person" value={a.contact_person} onChange={e => updateAddr(i, { contact_person: e.target.value })} className={inp} />
                  <input placeholder="Phone" value={a.phone} onChange={e => updateAddr(i, { phone: e.target.value })} className={inp} />
                  <input type="email" placeholder="Email" value={a.email} onChange={e => updateAddr(i, { email: e.target.value })} className={inp} />
                </div>
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-[var(--border)]">
                  <div className="flex items-center gap-2">
                    <button data-testid={`sup-primary-bill-${i}`} onClick={() => setPrimaryBill(i)}
                      className={`h-7 px-2 text-[10px] font-mono uppercase tracking-widest rounded-sm border flex items-center gap-1 ${a.is_primary_bill ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>
                      <StarIcon size={10} weight={a.is_primary_bill ? "fill" : "regular"} />Primary Bill
                    </button>
                    <button data-testid={`sup-primary-ship-${i}`} onClick={() => setPrimaryShip(i)}
                      className={`h-7 px-2 text-[10px] font-mono uppercase tracking-widest rounded-sm border flex items-center gap-1 ${a.is_primary_ship ? "border-sky-500 bg-sky-500/15 text-sky-300" : "border-[var(--border)] text-zinc-400 hover:text-white"}`}>
                      <TruckIcon size={10} weight={a.is_primary_ship ? "fill" : "regular"} />Primary Ship
                    </button>
                  </div>
                  <button data-testid={`sup-delete-addr-${i}`} onClick={() => deleteAddress(i)}
                    className="h-7 px-2 text-[10px] uppercase tracking-widest rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1">
                    <TrashIcon size={11} weight="bold" />Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Cancel</button>
          <button data-testid="sup-save" onClick={save} disabled={saving} className="h-9 px-4 bg-[var(--accent)] hover:opacity-90 text-white text-sm font-semibold rounded-sm disabled:opacity-50">
            {saving ? "Saving…" : editing ? "Update Supplier" : "Create Supplier"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
